/**
 * A move that names a person rather than a card.
 *
 * Every move the renderer had before this either names a card, asks a question
 * from `prompts`, or takes no argument at all - so a move carrying a seat drew
 * as nothing at all: the hand reads `args[0]` as a card id, looks for a card
 * called "2", and finds none. See `seatActions` in the toolkit's layout for
 * why it is declared rather than sniffed.
 *
 * Nothing here knows what any of these moves MEAN. The two questions it
 * answers are "which seats is it offered on" - which the server has already
 * decided - and "is this seat's head start still running".
 */

import type { LegalMove } from '../net/match.js';

/** Seat number to that seat's count, or to a moment in time. */
export interface BySeat {
  [seat: number]: number;
}

/**
 * What was last seen, and when each seat's head start runs out.
 *
 * Kept together because the window starts on a TRANSITION - a seat arriving at
 * one card - and a transition cannot be read from the present alone.
 */
export interface GraceMemory {
  readonly counts: BySeat;
  readonly until: BySeat;
}

export const NO_GRACE: GraceMemory = { counts: {}, until: {} };

/** The seats a move is offered on, straight off the wire. */
export function seatsOffered(legalMoves: readonly LegalMove[], move: string): Set<number> {
  const seats = new Set<number>();
  for (const legal of legalMoves) {
    if (legal.move !== move) continue;
    const first = legal.args[0];
    if (typeof first !== 'string') continue;
    const seat = parseInt(first, 10);
    if (!Number.isNaN(seat)) seats.add(seat);
  }
  return seats;
}

/**
 * Advance the head starts, given what every seat is now holding.
 *
 * Three cases, and the middle one is the one worth stating:
 *
 *   - a seat **arrives** at the watched count, and its window opens;
 *   - a seat **stays** there, and keeps whatever is left of the window it
 *     already had - restarting it on every unrelated redraw would hold the
 *     others for as long as somebody sat on one card;
 *   - a seat **leaves**, and the window closes with it, because there is
 *     nothing left to be first to say.
 *
 * A seat seen for the first time counts as arriving. That is a window opened
 * by somebody else's page loading, which is a second and a half of nobody
 * being able to call a seat that may have been sitting there for a minute -
 * and the alternative is a player who joins late being able to snipe
 * instantly, which is worse.
 */
export function updateGrace(
  previous: GraceMemory,
  counts: BySeat,
  spec: { count: number; ms: number },
  now: number,
): GraceMemory {
  const until: BySeat = {};

  for (const key of Object.keys(counts)) {
    const seat = Number(key);
    if (counts[seat] !== spec.count) continue;

    const before = previous.counts[seat];
    const running = previous.until[seat];
    until[seat] = before === spec.count && running !== undefined ? running : now + spec.ms;
  }

  return { counts: { ...counts }, until };
}

/** Whether this seat's head start is still running. */
export function isHeld(memory: GraceMemory, seat: number, now: number): boolean {
  const until = memory.until[seat];
  return until !== undefined && now < until;
}

/** The soonest a held seat frees up, or null when none is held. */
export function nextRelease(memory: GraceMemory, now: number): number | null {
  let soonest: number | null = null;
  for (const key of Object.keys(memory.until)) {
    const until = memory.until[Number(key)] as number;
    if (until <= now) continue;
    if (soonest === null || until < soonest) soonest = until;
  }
  return soonest;
}
